/**
 * Turning caught exceptions into copy we are willing to show a user.
 *
 * The rule this exists to enforce: a raw exception message never reaches the
 * DOM. Whatever the fetch layer produced — an HTTP status line, flattened
 * pydantic validation output, an nginx HTML page, a `TypeError` from a dropped
 * connection — is useful in a log and useless (often alarming) on screen.
 *
 * Call sites should not read `error.message` or `String(err)`. They call
 * `toUserMessage(err, t)` and render what comes back.
 */

import type { TFunction } from 'i18next';

/**
 * Status → translation key. Keys are namespace-qualified (`common:`) so they
 * resolve the same whether the caller's `useTranslation()` was scoped to
 * `common`, `assist`, `setup` or anything else.
 */
const STATUS_KEYS: Record<number, string> = {
  400: 'common:errors.badRequest',
  401: 'common:errors.sessionExpired',
  403: 'common:errors.forbidden',
  404: 'common:errors.notFound',
  408: 'common:errors.timeout',
  409: 'common:errors.conflict',
  413: 'common:errors.tooLarge',
  422: 'common:errors.validation',
  429: 'common:errors.rateLimited',
};

const SERVER_KEY = 'common:errors.server';
const NETWORK_KEY = 'common:errors.network';
const GENERIC_KEY = 'common:errors.generic';

/**
 * How each engine words a `fetch` rejection when the request never completed —
 * offline, DNS failure, connection reset, CORS rejection.
 */
const NETWORK_ERROR_PATTERN =
  /failed to fetch|networkerror|network request failed|load failed|fetch failed|connection/i;

/**
 * `fetch` rejects with a bare `TypeError` on a transport failure, and the
 * browser's own wording is exactly the kind of string this module keeps off
 * the screen.
 *
 * Matching on `TypeError` alone would be wrong: `Cannot read properties of
 * undefined` is also a `TypeError`, so an ordinary frontend crash would tell
 * the user to check their connection and send them off debugging their wifi
 * instead of surfacing our bug. Require the wording too — a misattributed
 * error is worse than a generic one.
 */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && NETWORK_ERROR_PATTERN.test(err.message);
}

/** The shape this module needs from an API failure. */
interface ApiErrorLike {
  status: number;
  userMessage?: string;
}

/**
 * Recognise an `ApiError` structurally rather than with `instanceof`.
 *
 * `instanceof` would couple this module to the api-client module *object*, and
 * 41 test files replace that module with `vi.mock`. Most do not re-export
 * `ApiError`, so the check would compare against `undefined` and throw —
 * turning a mocked API call in an unrelated test into a crash inside error
 * handling. Duck-typing keeps the helper independent of module identity, which
 * also makes it correct if two copies of the client are ever bundled.
 */
function asApiError(err: unknown): ApiErrorLike | undefined {
  if (!err || typeof err !== 'object') return undefined;

  const candidate = err as { name?: unknown; status?: unknown; userMessage?: unknown };
  if (candidate.name !== 'ApiError') return undefined;
  if (typeof candidate.status !== 'number') return undefined;

  return {
    status: candidate.status,
    userMessage: typeof candidate.userMessage === 'string' ? candidate.userMessage : undefined,
  };
}

export interface ToUserMessageOptions {
  /**
   * Translation key for the copy to use when nothing more specific applies.
   * Pass a screen-specific key ("we couldn't load your surveys") to say
   * something more useful than the generic fallback.
   */
  fallbackKey?: string;
  /**
   * Where this happened, for the log line only. Never rendered.
   */
  context?: string;
}

/**
 * Log the technical detail so it stays diagnosable, since it is deliberately
 * not shown to the user. This is the only place the raw message is allowed to
 * travel.
 */
export function logTechnicalError(err: unknown, context?: string): void {
  console.error(context ? `[${context}]` : '[error]', err);
}

/**
 * Map a caught error to copy that is safe to render.
 *
 * Order of preference:
 *   1. Server-authored copy, when the response carried a deliberate message
 *      for a 4xx (`ApiError.userMessage` — see `curatedUserMessage` in the API
 *      client for what qualifies). This is how genuinely useful, specific
 *      messages like "This invitation has expired" still reach the user.
 *   2. Our own translated copy for the status.
 *   3. The caller's fallback key, else a generic apology.
 */
export function toUserMessage(
  err: unknown,
  t: TFunction,
  options: ToUserMessageOptions = {},
): string {
  const { fallbackKey = GENERIC_KEY, context } = options;

  logTechnicalError(err, context);

  const apiError = asApiError(err);
  if (apiError) {
    if (apiError.userMessage) return apiError.userMessage;

    const statusKey = STATUS_KEYS[apiError.status];
    if (statusKey) return t(statusKey);
    if (apiError.status >= 500) return t(SERVER_KEY);

    return t(fallbackKey);
  }

  if (isNetworkError(err)) return t(NETWORK_KEY);

  return t(fallbackKey);
}
